// Dashboard API client. All calls pass X-Admin-Secret from sessionStorage.
// Worker URL comes from VITE_WORKER_URL at build time.

const STORAGE_KEY = "ea_admin_secret";
const WORKER_URL = import.meta.env.VITE_WORKER_URL ?? "";

export function getStoredSecret(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}

export function storeSecret(s: string): void {
  sessionStorage.setItem(STORAGE_KEY, s);
}

export function clearSecret(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  const secret = getStoredSecret();
  if (!secret) throw new ApiError(401, "no admin secret stored");
  const init: RequestInit = {
    method,
    headers: {
      "X-Admin-Secret": secret,
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(extraHeaders ?? {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  };
  const res = await fetch(`${WORKER_URL}${path}`, init);
  const text = await res.text();
  const data = text ? safeParse(text) : null;
  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? `${method} ${path} failed (${res.status})`;
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

function safeParse(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

// Lightweight health-style probe used by the login gate to validate the secret.
export async function probe(): Promise<boolean> {
  try {
    await call<{ tenants: unknown[] }>("GET", "/api/tenants");
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return false;
    throw e;
  }
}

// --------------- Tenants ---------------
export interface TenantRow {
  tenant_id: string;
  display_name: string;
  client_id: string;
  cold_days_threshold: number;
  min_file_size_bytes: number;
  max_file_size_bytes?: number;
  archive_enabled: number;
  archive_mode: "delete" | "hide";
  spfx_deployed: number;
  excluded_paths: string;
  excluded_extensions: string;
  glacier_enabled?: number;
  glacier_age_days?: number;
  glacier_idle_days?: number;
  // Backup + archive policy (Phases 1–2), editable per tenant.
  backup_enabled?: number;
  archive_trigger_pct?: number;
  archive_target_pct?: number;
  archive_min_idle_days?: number;
  backup_capture_native_versions?: number;
  // Retention policy (Phase 3), editable per tenant.
  retention_enabled?: number;
  retention_scope?: "tenant" | "containers";
  retention_container_ids?: string | null;
  retention_keep_days?: number | null;
  retention_two_person?: number;
  retention_monthly_ceiling_aud?: number;
  kek_destroyed_at?: string | null;
  total_bytes_archived: number;
  total_files_archived: number;
  estimated_monthly_saving_aud: number;
  estimated_annual_saving_aud: number;
}

export const listTenants  = () => call<{ tenants: TenantRow[] }>("GET", "/api/tenants");
export const patchTenant  = (id: string, patch: Partial<TenantRow>) =>
  call<{ tenant: TenantRow }>("PATCH", `/api/tenants/${id}`, patch);
export const upsertTenant = (t: Partial<TenantRow>) =>
  call<{ tenant: TenantRow }>("POST", "/api/tenants", t);

// --------------- Jobs / Files / Restores / Savings ---------------
export interface JobRow {
  id: number; tenant_id: string; kind: string; trigger: string; status: string;
  started_at: string; finished_at: string | null;
  files_scanned: number; files_queued: number; files_archived: number;
  bytes_archived: number; errors: number; error_message: string | null;
}
export const listJobs = (params: Record<string, string> = {}) =>
  call<{ jobs: JobRow[] }>("GET", `/api/jobs${qs(params)}`);

export interface FileRow {
  id: number; public_id: string; tenant_id: string; site_id: string;
  drive_id: string; original_item_id: string; stub_item_id: string | null;
  original_path: string; filename: string; r2_key: string; size_bytes: number;
  content_type: string | null; archived_at: string; status: string;
  bytes_saved_cost: number; backend?: "wasabi" | "glacier";
  last_restored_at?: string | null;
  glacier_thaw_started_at?: string | null;
  glacier_thaw_ready_at?: string | null;
  kek_version?: number; cipher?: string;
}
export const listFiles = (params: Record<string, string> = {}) =>
  call<{ files: FileRow[] }>("GET", `/api/files${qs(params)}`);

export interface RestoreRow {
  id: number; archived_file_id: number; requested_by_email: string | null;
  source: string; status: string; requested_at: string; completed_at: string | null;
  filename: string; tenant_id: string; original_path: string;
}
export const listRestores = (params: Record<string, string> = {}) =>
  call<{ restores: RestoreRow[] }>("GET", `/api/restores${qs(params)}`);

export interface SavingsRow {
  tenant_id: string; display_name: string;
  total_bytes_archived: number; total_files_archived: number;
  estimated_monthly_saving_aud: number; estimated_annual_saving_aud: number;
  last_recomputed_at: string;
}
export const listSavings = () => call<{ savings: SavingsRow[] }>("GET", "/api/savings");

// --------------- Sign-url / Tiering / Audit ---------------
export const signUrl = (file_id: number, ttl_seconds = 3600) =>
  call<{ url: string; expires: number; ttl_seconds: number }>("POST", "/api/sign-url", { file_id, ttl_seconds });

export const tierNow = (tenant_id: string) =>
  call<{ tenant_id: string; job_id: number; files_tiered: number; bytes_tiered: number; errors: number }>("POST", "/api/tier-now", { tenant_id });

export const tierFile = (file_id: number) =>
  call<{ ok: boolean; file_id: number; backend: "glacier"; bytes_tiered: number }>("POST", `/api/files/${file_id}/tier`);

export const thawStatus = (file_id: number) =>
  call<{
    backend: "wasabi" | "glacier";
    tier?: string;
    exists?: boolean;
    thawing?: boolean;
    ready?: boolean;
    thaw_started_at?: string | null;
    thaw_ready_at?: string | null;
    thaw_expires_at?: string | null;
  }>("GET", `/api/files/${file_id}/thaw-status`);

export type AuditTable =
  | "archive_jobs" | "restore_requests" | "archive_job_events" | "restore_request_events"
  | "retention_restore_requests" | "retention_restore_events" | "integrity_checks";
export const verifyAudit = (table: AuditTable) =>
  call<{ ok: boolean; table: AuditTable; rowsChecked: number; brokenAtId?: number }>("GET", `/api/audit/verify?table=${table}`);

export const triggerScan    = (tenant_id: string) => call<unknown>("POST", "/scan",    { tenant_id });
export const recomputeSavings = (tenant_id: string) => call<unknown>("POST", "/api/recompute-savings", { tenant_id });

// Backup (Phase 1) + engine archive (Phase 2).
export const archiveRun = (tenant_id: string) =>
  call<{ ok: boolean; archived: number; errors: number }>("POST", "/api/archive/run", { tenant_id });
export const enrollBackup = (tenant_id: string, site_id: string, drive_id: string) =>
  call<{ ok: boolean; scope_id: number; seeded: boolean }>("POST", "/api/backup/enroll", { tenant_id, site_id, drive_id });

// --------------- Retention (Phase 3) ---------------
export interface RetentionSnapshotView {
  public_id: string; scope: string; captured_at: string; file_count: number;
  size_bytes: number; status: string; expires_at: string | null; legal_hold: number; packs: number;
}
export interface RetentionApproverRow { id: number; tenantId: string; entraGroupId: string; label?: string | null }
export interface RetentionRestoreView {
  public_id: string; status: string; est_bytes: number; est_cost_aud: number;
  approved_by: string[]; requested_at: string; completed_at: string | null;
}
export interface RetentionRestoreEventView { event: string; status?: string | null; detail?: string | null; errorMessage?: string | null }

export const snapshotNow = (tenant_id: string, as_of?: string) =>
  call<{ ok: boolean; snapshotPublicId: string; fileCount: number; sizeBytes: number; packs: number }>(
    "POST", "/api/retention/snapshot", { tenant_id, ...(as_of ? { as_of } : {}) });

export const listRetentionSnapshots = (tenant_id: string) =>
  call<{ snapshots: RetentionSnapshotView[] }>("GET", `/api/retention/snapshots${qs({ tenant_id })}`);

export const listRetentionApprovers = (tenant_id: string) =>
  call<{ approvers: RetentionApproverRow[] }>("GET", `/api/retention/approvers${qs({ tenant_id })}`);
export const addRetentionApprover = (tenant_id: string, entra_group_id: string, label?: string) =>
  call<{ ok: boolean; id: number }>("POST", "/api/retention/approvers", { tenant_id, entra_group_id, label });
export const removeRetentionApprover = (tenant_id: string, id: number) =>
  call<{ ok: boolean }>("DELETE", `/api/retention/approvers/${id}${qs({ tenant_id })}`);

export const requestRetentionRestore = (tenant_id: string, snapshot_public_id: string, selector?: { wholeScope?: boolean; paths?: string[] }) =>
  call<{ ok: boolean; request: { public_id: string; status: string; est_bytes: number; est_cost_aud: number } }>(
    "POST", "/api/retention/restore", { tenant_id, snapshot_public_id, ...(selector ? { selector } : {}) });

// Approve requires a DELEGATED Entra token (approver's own sign-in), passed as Bearer.
export const approveRetentionRestore = (request_public_id: string, tenant_id: string, delegatedToken: string, approver_oked_cost: boolean) =>
  call<{ ok: boolean; status: string; reason?: string; awaitingSecondApprover?: boolean }>(
    "POST", `/api/retention/restore/${request_public_id}/approve`, { tenant_id, approver_oked_cost }, { Authorization: `Bearer ${delegatedToken}` });

export const retentionRestoreStatus = (request_public_id: string) =>
  call<{ ok: boolean; request: RetentionRestoreView; events: RetentionRestoreEventView[] }>(
    "GET", `/api/retention/restore/${request_public_id}`);

export const executeRetentionRestore = (request_public_id: string, tenant_id: string) =>
  call<{ ok: boolean; status: string; ready: boolean; files: Array<{ path: string; size: number; content_type: string }> }>(
    "POST", `/api/retention/restore/${request_public_id}/execute`, { tenant_id });

// --------------- Self-test (engine smoke) ---------------
export interface SelfTestResult {
  ok: boolean;
  tenant?: string;
  sizeBytes?: number;
  error?: string;
  checks?: Array<{ name: string; ok: boolean; detail?: string }>;
  cleanup?: { packsDeleted: number };
}
export const runSelfTest = (bytes = 4 * 1024 * 1024) =>
  call<SelfTestResult>("POST", `/api/selftest?bytes=${bytes}`);

function qs(params: Record<string, string>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== "" && v != null);
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries).toString();
}
